import RefreshIcon from '@mui/icons-material/Refresh'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { format } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, concatMap, map } from 'rxjs/operators'
import { refreshInsights } from 'services/post/refreshInsights/actions'
import { message } from 'services/snackbar'
import { BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_LINKEDIN, BRAND_PINTEREST } from 'shared/constants'
import { PostDestination, PostDestinationType } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { HOURS_IN_DAY, MIN_IN_HOUR, MS_IN_SEC, SEC_IN_MIN } from 'utils/constants'
import { getInsightsRefreshRetryAt, setInsightsRefreshRetryAt } from 'utils/insights-refresh-store'
import styles from './RefreshStatsButton.pcss'

const SUPPORTED_NETWORKS: PostDestinationType[] = [BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_LINKEDIN, BRAND_PINTEREST]
const RETRY_DATE_FORMAT = 'MMM d, h:mm a'
// The server always returns `retryAt` on success. If it ever omits it we still disable locally,
// so the user isn't invited to spend a second request the server would only reject.
const FALLBACK_COOLDOWN_SEC = SEC_IN_MIN * MIN_IN_HOUR * HOURS_IN_DAY

export function formatRetryAt(retryAt?: number | null) {
  return retryAt ? format(new Date(retryAt * MS_IN_SEC), RETRY_DATE_FORMAT) : ''
}

interface RefreshStatsButtonProps {
  selectedProfiles: PostDestination[]
  className?: string
}

export function RefreshStatsButton(props: RefreshStatsButtonProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()

  const { selectedProfiles } = props
  const profile = selectedProfiles[0]
  const ppid = profile?.ppid
  const multipleSelected = selectedProfiles.length > 1
  // The store already carries per-profile connection state, so a disconnected profile can be
  // reported before spending a request. Only an explicit `false` counts as disconnected — an
  // absent field must never block a refresh.
  const tokenExpired = profile?.connected === false

  const [retryAt, setRetryAt] = useState<number | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setRetryAt(ppid && !multipleSelected ? getInsightsRefreshRetryAt(ppid) : null)
  }, [ppid, multipleSelected])

  const ppidRef = useRef<string | undefined>(ppid)

  useEffect(() => {
    ppidRef.current = ppid
  }, [ppid])

  // Guards against a double-click firing two requests. `concatMap` serializes them but does not
  // dedupe, so the second would come back `rate-limited` right after the first reported success.
  // A ref rather than the `pending` state because two clicks in one tick see the same batched state.
  const inFlightRef = useRef(false)
  const refresh$ = useRef<Subject<string>>(undefined)

  useEffect(() => {
    refresh$.current = new Subject<string>()
    refresh$.current
      .pipe(concatMap((requestPpid: string) => dispatch(refreshInsights(requestPpid))
        .pipe(
          catchError((error: any) => {
            const msg = error?.response?.error?.message
            if (msg) {
              dispatch(message(msg, 'error', NOTIFICATION_DURATION_LONG))
            } else {
              dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
            }
            return observableOf({ requestFailed: true })
          }),
          map((response: any) => ({ ...response, requestPpid }))
        )
      ))
      .subscribe((response: any) => {
        inFlightRef.current = false
        setPending(false)

        if (response.requestFailed) {
          return
        }

        // A refresh the server could not even attempt must not start a 24h cooldown, so the user can
        // retry as soon as the profile is reconnected. Guarded here rather than relying on the server
        // omitting `retryAt`, because caching one would lock the button for a day for no reason.
        const nextRetryAt = response.error === 'token-expired'
          ? undefined
          : response.retryAt
            || (response.success ? Math.floor(Date.now() / MS_IN_SEC) + FALLBACK_COOLDOWN_SEC : undefined)
        if (nextRetryAt) {
          setInsightsRefreshRetryAt(response.requestPpid, nextRetryAt)
          if (response.requestPpid === ppidRef.current) {
            setRetryAt(nextRetryAt)
          }
        }

        if (response.success) {
          if (response.queued > 0) {
            dispatch(message(
              intl.formatMessage({ id: 'post.notifications.refresh-stats-queued' }, { count: response.queued }),
              'success',
              NOTIFICATION_DURATION_LONG
            ))
          } else {
            dispatch(message(
              intl.formatMessage({ id: 'post.notifications.refresh-stats-none' }),
              'warning',
              NOTIFICATION_DURATION_LONG
            ))
          }
          return
        }

        if (response.error === 'token-expired') {
          dispatch(message(
            intl.formatMessage({ id: 'post.notifications.refresh-stats-token-expired' }),
            'warning',
            NOTIFICATION_DURATION_LONG
          ))
          return
        }

        if (response.error === 'rate-limited') {
          dispatch(message(
            intl.formatMessage(
              { id: 'post.notifications.refresh-stats-rate-limited' },
              { date: formatRetryAt(nextRetryAt) }
            ),
            'warning',
            NOTIFICATION_DURATION_LONG
          ))
          return
        }

        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
      })

    return () => refresh$.current?.unsubscribe()
  }, [dispatch, intl])

  const buildDisabledMessage = useCallback((currentRetryAt: number | null) => {
    if (multipleSelected) {
      return intl.formatMessage({ id: 'post.notifications.refresh-stats-single-profile' })
    }
    // Ahead of the cooldown branch on purpose: a disconnected profile can also carry a cached
    // `retryAt` from an earlier refresh, and reporting that as "already refreshed recently" would
    // claim a success that never happened and hide the reason the stats are stale.
    if (tokenExpired) {
      return intl.formatMessage({ id: 'post.notifications.refresh-stats-token-expired' })
    }
    if (currentRetryAt) {
      return intl.formatMessage(
        { id: 'post.notifications.refresh-stats-rate-limited' },
        { date: formatRetryAt(currentRetryAt) }
      )
    }
    return undefined
  }, [multipleSelected, tokenExpired, intl])

  const cooldownActive = Boolean(retryAt)
  const disabled = multipleSelected || tokenExpired || cooldownActive || pending
  const disabledMessage = buildDisabledMessage(retryAt)

  const onClick = useCallback(() => {
    if (inFlightRef.current) {
      return
    }

    // Re-read from the store rather than trusting the stale `disabled`/`retryAt` state — the
    // cooldown store self-prunes elapsed entries on read, so this is the only reliable way to
    // tell whether a cooldown set earlier in this session has actually elapsed by now.
    const current = ppid ? getInsightsRefreshRetryAt(ppid) : null
    if (current !== retryAt) {
      setRetryAt(current)
    }

    const currentDisabled = multipleSelected || tokenExpired || Boolean(current)
    if (currentDisabled) {
      const currentMessage = buildDisabledMessage(current)
      if (currentMessage) {
        dispatch(message(currentMessage, 'warning', undefined, true))
      }
      return
    }

    if (!ppid) {
      return
    }

    inFlightRef.current = true
    setPending(true)
    refresh$.current?.next(ppid)
  }, [ppid, retryAt, multipleSelected, tokenExpired, buildDisabledMessage, dispatch])

  const networkSupported = profile ? SUPPORTED_NETWORKS.includes(profile.type) : false
  const visible = selectedProfiles.length === 1 && networkSupported

  if (!visible) {
    return null
  }

  return (
    <Tooltip title={disabledMessage || <FormattedMessage id="post.tooltip.refresh-stats" />}>
      <span className={`${styles.container} ${props.className || ''}`}>
        <Button
          data-testid="refresh-stats-button"
          aria-disabled={disabled}
          startIcon={<RefreshIcon className={styles['icon-refresh']} />}
          className={`${styles['refresh-stats-button']} ${disabled ? styles.disabled : ''}`}
          onClick={onClick}
        >
          <FormattedMessage id="post.actions.refresh-stats" />
        </Button>
      </span>
    </Tooltip>
  )
}

export default RefreshStatsButton
